/**
 * ManifestConditionEvaluator
 *
 * Thin wrapper around `fable.ExpressionParser` that lets a form manifest gate
 * top-level lifecycle steps with a solver expression. Used by the base dynamic
 * form to decide whether to:
 *
 *   - skip the `InitialBundle` data fetch  (`shouldSkipInitialBundle`)
 *   - skip the post-load `solve()` pass    (`shouldSkipSolveOnLoad`)
 *
 * The expressions are the same Fable solver language the manifest's section
 * `Solvers: [...]` arrays already use, so form authors only have to know one
 * language. See `/Pavia/retold/modules/fable/fable/source/services/Fable-Service-ExpressionParser.js`
 * for the parser; `solve(expression, dataSource, results, manifest)` is the
 * public entry point.
 *
 * Two manifest shapes are honored per gating step:
 *
 *   - **Sugar:** `SkipInitialBundleWhenLocked: true` / `SkipSolveWhenLocked: true`
 *     Shorthand for "skip when the document is locked." Internally resolves to
 *     a `WHEN(AppData.DocumentData.ReportData.Locked, 1, 0)` expression so
 *     truthy-Locked returns truthy, falsy-Locked / missing-Locked returns 0.
 *     The solve sugar defaults to **true** (omitting `SkipSolveWhenLocked`
 *     skips solve on locked loads); the bundle sugar defaults to false.
 *
 *   - **General:** `SkipInitialBundleWhen: "<expression>"` / `SkipSolveWhen: "<expression>"`
 *     Any Fable solver expression. Evaluated against the supplied scope object
 *     (typically `{ AppData: ... }`). Truthy result → skip.
 *
 * When both are present, OR semantics apply — either matching is enough.
 *
 * Failure modes are fail-safe — a missing parser, a malformed expression, or a
 * thrown error during evaluation all resolve to "do not skip." We never skip
 * caller work unless the manifest is unambiguously correct and the expression
 * evaluates truthy.
 *
 * NB: The Fable solver returns numeric results as strings (`"1"` / `"0"`), so
 * the `_isTruthy` helper coerces strings: `"0"` and `""` are falsy, anything
 * else non-empty is truthy.
 */

const LOCKED_ADDRESS = 'AppData.DocumentData.ReportData.Locked';
const LOCKED_SUGAR_EXPRESSION = `WHEN(${LOCKED_ADDRESS}, 1, 0)`;

/**
 * Resolve `AppData.DocumentData.ReportData.Locked` from the scope using native
 * JavaScript truthiness — `0`, `"0"`, `false`, `null`, `undefined`, `""`, `NaN`
 * are all falsy.
 *
 * The sugar form (`SkipInitialBundleWhenLocked: true` / `SkipSolveWhenLocked: true`)
 * intentionally bypasses Fable's `WHEN(...)` for the resolution: Fable's
 * `Logic.when` checks truthiness with `!pValue`, which evaluates the *string*
 * `"0"` as truthy because non-empty strings are JS-truthy. The expression
 * parser stringifies numeric address values before passing them to `Logic.when`,
 * so a numeric `0` from the database (the common case for a draft document's
 * `Locked` TINYINT) was being mis-classified as "locked" and the InitialBundle
 * was being skipped on draft loads. See the test suite's "treats numeric 0 as
 * unlocked" case for the regression guard.
 *
 * @param {Object} pScope - The runtime scope (typically `{ AppData: ... }`).
 * @returns {boolean} `true` when the document is meaningfully locked.
 */
function _resolveLockedTruthy(pScope)
{
	const tmpLocked = pScope?.AppData?.DocumentData?.ReportData?.Locked;
	// Defensively treat the literal string "0" as falsy in addition to native
	// JS-falsy values. A TINYINT `Locked` column normally arrives as numeric 0/1,
	// but some serialization paths (older Meadow versions, JSON-stringified
	// snapshots) can deliver it as the string "0" / "1" — and `Boolean("0")` is
	// `true` in JS, which would re-introduce the same Locked=0 misclassification
	// we're fixing here.
	if (tmpLocked === '0')
	{
		return false;
	}
	return Boolean(tmpLocked);
}

/**
 * Coerce the parser's string-typed numeric output (or any other return shape)
 * to a boolean. Treats `"0"`, `""`, `null`, `undefined`, `false`, `NaN` as
 * falsy; anything else truthy.
 *
 * @param {*} pValue
 * @returns {boolean}
 */
function _isTruthy(pValue)
{
	if (pValue === null || pValue === undefined || pValue === false)
	{
		return false;
	}
	if (typeof pValue === 'number')
	{
		return pValue !== 0 && !Number.isNaN(pValue);
	}
	if (typeof pValue === 'string')
	{
		return pValue.length > 0 && pValue !== '0';
	}
	return Boolean(pValue);
}

/**
 * Evaluate a single Fable solver expression against the supplied scope.
 *
 * @param {string | null | undefined} pExpression - The solver expression to evaluate.
 * @param {Object} pScope - Data source object the parser dereferences against
 *   (typically `{ AppData: <runtime AppData> }`).
 * @param {*} pFable - The Fable instance, used to reach `ExpressionParser`.
 *   Optional — when absent the function fails closed (returns false).
 * @returns {boolean} `true` when the expression evaluates truthy.
 */
function evaluateExpression(pExpression, pScope, pFable)
{
	if (typeof pExpression !== 'string' || pExpression.length === 0)
	{
		return false;
	}
	if (!pFable)
	{
		return false;
	}
	let tmpParser = pFable.ExpressionParser;
	if (!tmpParser && typeof pFable.instantiateServiceProviderIfNotExists === 'function')
	{
		try
		{
			pFable.instantiateServiceProviderIfNotExists('ExpressionParser');
			tmpParser = pFable.ExpressionParser;
		}
		catch (pError)
		{
			return false;
		}
	}
	if (!tmpParser || typeof tmpParser.solve !== 'function')
	{
		return false;
	}
	try
	{
		const tmpResult = tmpParser.solve(pExpression, pScope || {}, {});
		return _isTruthy(tmpResult);
	}
	catch (pError)
	{
		if (pFable.log && typeof pFable.log.warn === 'function')
		{
			pFable.log.warn(`ManifestConditionEvaluator: expression failed to evaluate (${pError && pError.message ? pError.message : pError}). Expression: ${pExpression}`);
		}
		return false;
	}
}

/**
 * Decide whether to skip the InitialBundle fetch for the current load.
 *
 * Honors two manifest-level keys:
 *   - `SkipInitialBundleWhenLocked: true` — sugar; resolves to a Locked check.
 *   - `SkipInitialBundleWhen: "<expression>"` — general Fable solver expression.
 *
 * OR semantics across both — either truthy → skip.
 *
 * @param {Object | null | undefined} pManifest
 * @param {Object} pScope - The runtime scope (typically `{ AppData: ... }`).
 * @param {*} pFable - The Fable instance carrying ExpressionParser.
 * @returns {boolean}
 */
function shouldSkipInitialBundle(pManifest, pScope, pFable)
{
	if (!pManifest)
	{
		return false;
	}
	if (pManifest.SkipInitialBundleWhenLocked === true && _resolveLockedTruthy(pScope))
	{
		return true;
	}
	if (typeof pManifest.SkipInitialBundleWhen === 'string' && pManifest.SkipInitialBundleWhen.length > 0)
	{
		return evaluateExpression(pManifest.SkipInitialBundleWhen, pScope, pFable);
	}
	return false;
}

/**
 * Decide whether to skip the post-data-load `solve()` pass for the current
 * load. Used by snapshot forms that must not have stored values overwritten by
 * re-running the manifest's solver expressions on a Locked re-open.
 *
 * Honors two manifest-level keys:
 *   - `SkipSolveWhenLocked` — sugar; resolves to a Locked check. **Defaults to
 *     true**: a locked load skips solve unless the manifest explicitly sets
 *     `SkipSolveWhenLocked: false` (persisted solver outputs are part of the
 *     locked snapshot; re-deriving them from current state is the exception,
 *     not the rule).
 *   - `SkipSolveWhen: "<expression>"` — general Fable solver expression.
 *
 * OR semantics across both.
 *
 * @param {Object | null | undefined} pManifest
 * @param {Object} pScope
 * @param {*} pFable
 * @returns {boolean}
 */
function shouldSkipSolveOnLoad(pManifest, pScope, pFable)
{
	if (!pManifest)
	{
		return false;
	}
	if (pManifest.SkipSolveWhenLocked !== false && _resolveLockedTruthy(pScope))
	{
		return true;
	}
	if (typeof pManifest.SkipSolveWhen === 'string' && pManifest.SkipSolveWhen.length > 0)
	{
		return evaluateExpression(pManifest.SkipSolveWhen, pScope, pFable);
	}
	return false;
}

module.exports = {
	evaluateExpression,
	shouldSkipInitialBundle,
	shouldSkipSolveOnLoad,
	LOCKED_ADDRESS,
	LOCKED_SUGAR_EXPRESSION,
};
